import { useMemo, useState } from "react"

import { ReactComponent as SvgPhoto } from "@common_assets/svg/photo.svg"
import { Grid, Skeleton, Stack } from "@mui/material"
import { useQueryBankerGroups } from "api/reactQuery/queries/groups"
import GroupCard from "components/common/groupCard"
import GroupMembersDialog from "components/common/groupMembersDialog"
import NoElementsMessage from "components/common/listing/noElementsMessage"
import TransactionsDialogForm from "components/module/classStudentsTeacher/common/viewCommonElementsContainer/transactionsDialogForm/TransactionsDialogForm"
import { useClassId } from "hooks/navigation"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { IGroup } from "ts/interfaces/Group"

const GRID_SIZE = { mobile: 12, tablet: 6, desktop: 3 }

const BankerGroups = () => {
  const { t } = useTranslation()
  const classId = Number(useClassId())
  const queryClient = useQueryClient()

  const { data, isLoading } = useQueryBankerGroups({
    classId,
    options: { enabled: !!classId },
  })
  const groups = useMemo(() => {
    const fetched = data?.data ?? []
    return [...fetched].sort((a, b) =>
      a.scope === b.scope ? 0 : a.scope === "SCHOOL" ? 1 : -1,
    )
  }, [data])

  const [transactionGroup, setTransactionGroup] = useState<IGroup | null>(null)
  const [viewGroup, setViewGroup] = useState<IGroup | null>(null)

  if (isLoading) {
    return (
      <Grid container spacing="16px" mt="8px">
        {Array.from({ length: 8 }).map((_, index) => (
          <Grid key={index} size={GRID_SIZE}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
        ))}
      </Grid>
    )
  }

  if (groups.length === 0) {
    return (
      <NoElementsMessage
        boxStyle={{ py: { mobile: "16px", tablet: "62px" } }}
        Icon={<SvgPhoto />}
        title={t("group.emptyTitle")}
      />
    )
  }

  return (
    <Stack gap="16px">
      <Grid container spacing="16px">
        {groups.map((group) => (
          <Grid key={group.id} size={GRID_SIZE}>
            <GroupCard
              readOnly
              group={group}
              onCardClick={() => setTransactionGroup(group)}
              onViewMembers={() => setViewGroup(group)}
              showSendTransactionCta
            />
          </Grid>
        ))}
      </Grid>

      {transactionGroup && (
        <TransactionsDialogForm
          isOpen
          isBanker
          groupId={transactionGroup.id}
          action="group"
          defaultExpandedSections={["bonus"]}
          handleClose={() => setTransactionGroup(null)}
          onSuccess={() => {
            queryClient.removeQueries(["bankerClass"])
            setTransactionGroup(null)
          }}
        />
      )}

      {viewGroup && (
        <GroupMembersDialog
          open
          classId={classId}
          group={viewGroup}
          onClose={() => setViewGroup(null)}
        />
      )}
    </Stack>
  )
}

export default BankerGroups
